import { fetchApi, API_BASE_URL } from './api';

export interface PurchasingRuleRow {
  id: number;
  rule_type: string;
  scope_type: string;
  scope_id: number | null;
  scope_supplier_name?: string;
  scope_supplier_name_display?: string;
  scope_family_description?: string;
  scope_family_name_display?: string;
  scope_product_name_display?: string;
  scope_product_code_display?: string;
  parameters_json: Record<string, unknown>;
  priority: number;
  adjustment_strategy: string;
  active: boolean;
  rule_category?: string;
  valid_from?: string | null;
  valid_until?: string | null;
  priority_criterion?: string | null;
  description_for_user?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface PurchasingRuleChatResponse {
  reply_text: string;
  confirmation_question?: string | null;
  pending_rule?: Record<string, unknown> | null;
  pending_rules?: Record<string, unknown>[] | null;
  interpretation?: {
    rule_type: string | null;
    reason?: string;
    scope_id_resolved?: number | null;
    scope_supplier_name_used?: string;
    scope_family_description_used?: string;
    warning?: string;
  } | null;
  rule: PurchasingRuleRow | Record<string, unknown> | null;
  persisted: boolean;
}

const BASE = 'purchasing-rules';

const listRulesInflight = new Map<string, Promise<PurchasingRuleRow[]>>();
const listRulesMemoryCache = new Map<string, PurchasingRuleRow[]>();

export function clearPurchasingRulesListCache(): void {
  listRulesMemoryCache.clear();
}

export const purchasingRulesService = {
  async listRules(activeOnly = true): Promise<PurchasingRuleRow[]> {
    const inflightKey = String(activeOnly);
    const cached = listRulesMemoryCache.get(inflightKey);
    if (cached) {
      return cached;
    }

    const existing = listRulesInflight.get(inflightKey);
    if (existing) {
      return existing;
    }

    const request = (async () => {
      const res = await fetchApi<PurchasingRuleRow[] | { results: PurchasingRuleRow[] }>(`${BASE}/rules/`, {
        params: { active_only: activeOnly },
      });
      if (Array.isArray(res)) return res;
      if (res && typeof res === 'object' && Array.isArray((res as { results: PurchasingRuleRow[] }).results)) {
        return (res as { results: PurchasingRuleRow[] }).results;
      }
      return [];
    })();

    listRulesInflight.set(inflightKey, request);
    try {
      const rows = await request;
      listRulesMemoryCache.set(inflightKey, rows);
      return rows;
    } finally {
      if (listRulesInflight.get(inflightKey) === request) {
        listRulesInflight.delete(inflightKey);
      }
    }
  },

  async getRule(id: number): Promise<PurchasingRuleRow> {
    return fetchApi<PurchasingRuleRow>(`${BASE}/rules/${id}/`);
  },

  async createRule(payload: Partial<PurchasingRuleRow>): Promise<PurchasingRuleRow> {
    return fetchApi<PurchasingRuleRow>(`${BASE}/rules/`, {
      method: 'POST',
      body: payload,
    });
  },

  async updateRule(id: number, updates: Partial<PurchasingRuleRow>): Promise<PurchasingRuleRow> {
    return fetchApi<PurchasingRuleRow>(`${BASE}/rules/${id}/`, {
      method: 'PATCH',
      body: updates,
    });
  },

  async deleteRule(id: number): Promise<void> {
    await fetchApi<void>(`${BASE}/rules/${id}/`, { method: 'DELETE' });
  },

  async chat(
    message: string,
    persist: boolean,
    warehouseId?: number,
    confirm?: boolean,
    pendingRule?: Record<string, unknown> | null,
    locale?: string
  ): Promise<PurchasingRuleChatResponse> {
    const lang = locale ? String(locale).split('-')[0] : undefined;
    return fetchApi<PurchasingRuleChatResponse>(`${BASE}/chat/`, {
      method: 'POST',
      body: {
        message,
        persist,
        warehouse_id: warehouseId,
        ...(lang ? { locale: lang } : {}),
        ...(confirm && pendingRule ? { confirm: true, pending_rule: pendingRule } : pendingRule ? { pending_rule: pendingRule } : {}),
      },
    });
  },

  async getSuppliers(warehouseId?: number): Promise<Array<{ id: number; name: string }>> {
    const res = await fetchApi<{ suppliers: Array<{ id: number; name: string }> }>(`${BASE}/suppliers/`, {
      params: warehouseId !== undefined && warehouseId !== null ? { warehouse_id: warehouseId } : {},
    });
    return res.suppliers ?? [];
  },

  async getFamilies(warehouseId?: number): Promise<Array<{ id: number; name: string }>> {
    const res = await fetchApi<{ families: Array<{ id: number; name: string }> }>(`${BASE}/families/`, {
      params: warehouseId !== undefined && warehouseId !== null ? { warehouse_id: warehouseId } : {},
    });
    return res.families ?? [];
  },

  async uploadPackagingExcel(supplierId: number, file: File): Promise<{ ok: boolean; rows_imported: number }> {
    const formData = new FormData();
    formData.append('supplier_id', String(supplierId));
    formData.append('file', file);
    const token = localStorage.getItem('auth_token');
    const res = await fetch(`${API_BASE_URL}/purchasing-rules/packaging/upload/`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error((err as { error?: string }).error || res.statusText || 'Error al subir el Excel');
    }
    return res.json();
  },

  async listPromotions(activeOnly = false): Promise<PromotionRow[]> {
    const res = await fetchApi<{ promotions: PromotionRow[] }>(`${BASE}/promotions/`, {
      params: { active_only: activeOnly ? 'true' : 'false' },
    });
    return res.promotions ?? [];
  },

  async createPromotion(payload: PromotionCreatePayload): Promise<PromotionRow> {
    return fetchApi<PromotionRow>(`${BASE}/promotions/create/`, {
      method: 'POST',
      body: payload,
    });
  },

  async uploadPromotionsExcel(file: File): Promise<{ ok: boolean; rows_imported: number; errors?: string[] }> {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('auth_token');
    const res = await fetch(`${API_BASE_URL}/purchasing-rules/promotions/upload/`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error((err as { error?: string }).error || res.statusText || 'Error al subir el Excel');
    }
    return res.json();
  },

  async getPromotion(id: string): Promise<PromotionRow> {
    return fetchApi<PromotionRow>(`${BASE}/promotions/${id}/`);
  },

  async updatePromotion(id: string, updates: Partial<PromotionRow>): Promise<PromotionRow> {
    return fetchApi<PromotionRow>(`${BASE}/promotions/${id}/`, {
      method: 'PATCH',
      body: updates,
    });
  },

  async deletePromotion(id: string): Promise<void> {
    await fetchApi<void>(`${BASE}/promotions/${id}/`, { method: 'DELETE' });
  },
};

export interface PromotionRow {
  id: string;
  scope_type: string;
  scope_id: number | null;
  scope_code?: string | null;
  scope_name?: string | null;
  discount_pct: number;
  start_date: string | null;
  end_date: string | null;
  minimum_volume: number | null;
  promotion_price?: number | null;
}

export interface PromotionCreatePayload {
  scope_type: 'product' | 'product_family' | 'supplier';
  scope_id?: number;
  scope_code?: string;
  discount_pct: number;
  start_date?: string;
  end_date?: string;
  minimum_volume?: number;
}
